import React from 'react';

import { useIntl } from 'react-intl';

import { HamburgerButton } from 'components/app-header/mobile-header/hamburger-button';
import {
  Header,
  LinkNoDecoration,
  MenuButtonContainer,
  MobileHeaderContainer,
} from 'components/app-header/styled';
import {
  NO_NETWORK_BACKGROUND_COLOR,
  NoNetworkHeader,
} from 'components/error-network/no-network-header';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { Logo } from 'components/logo';
import { NotificationsButton } from 'components/notifications-button';
import { TopOrderStatusBar } from 'components/top-order-status-bar';
import { useTopOrderStatusBar } from 'components/top-order-status-bar/use-top-order-status-bar';
import { TopServiceMode } from 'components/top-service-mode';
import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import { useIsDesktopBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { useNetworkContext } from 'state/network';
import { useSmartAlert } from 'state/smart-alert';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import SignupMobileHeader from '../signup-mobile-header';
import { StyledCenterSpacer, StyledLogo, StyledRightSpacer } from '../styled';

import { CartButton } from './cart-button';
import { HeaderLoyaltyPointsButton } from './mobile-header-cta';

export const StandardMobileHeader = React.memo(({ hideTsm = false }: { hideTsm?: boolean }) => {
  const { isNewsFeedEnabled: enableContentCards } = useContentCardsNotificationsExperiment();
  const { noStoreSelected } = useStoreContext();
  const isDesktop = useIsDesktopBreakpoint();
  const { isNetworkConnected, isInternetReachable } = useNetworkContext();

  const topOrderStatusBarData = useTopOrderStatusBar();
  const showTopOrderStatusBar = !!topOrderStatusBarData.heading;

  const { formatMessage } = useIntl();

  const isMobile = useIsMobileBreakpoint();

  const { isInAuthFlow } = useAuthFlow();
  const { modalAuthIsOpen } = useAuthContext();

  const { tsmSmartAlert } = useSmartAlert();

  if (isInAuthFlow && !modalAuthIsOpen) {
    return <SignupMobileHeader />;
  }

  const normalBackground = theme.token('background-hero-light');

  const getLayoutContainerBackgroundColor = () => {
    if (showTopOrderStatusBar) {
      return primitive.bk.$crunchyGreen;
    }
    if (tsmSmartAlert) {
      return primitive.bk.$errorLight;
    }
    return primitive.$white;
  };

  const showNoNetworkHeader = !isNetworkConnected || !isInternetReachable;

  return (
    <MobileHeaderContainer
      accessibilityLabel={formatMessage({ id: 'primaryNavLinks' })}
      testID="mobile-header"
      background={showNoNetworkHeader && isMobile ? NO_NETWORK_BACKGROUND_COLOR : normalBackground}
    >
      {showNoNetworkHeader && <NoNetworkHeader />}
      <CenteredRowContainer standardPadding>
        <Header>
          <MenuButtonContainer>
            <HamburgerButton />
          </MenuButtonContainer>
          <StyledCenterSpacer>
            <LinkNoDecoration
              to={routes.base}
              alignSelf={'flex-start'}
              marginLeft={isDesktop ? 'auto' : '$8'}
            >
              <StyledLogo testID="mobile-header-logo">
                <Logo />
              </StyledLogo>
            </LinkNoDecoration>
          </StyledCenterSpacer>
          <StyledRightSpacer>
            <HeaderLoyaltyPointsButton />
            {enableContentCards && <NotificationsButton />}
            <CartButton />
          </StyledRightSpacer>
        </Header>
      </CenteredRowContainer>
      <CenteredRowContainer
        containerStyle={{
          backgroundColor: getLayoutContainerBackgroundColor(),
        }}
        standardPadding
      >
        {showTopOrderStatusBar && topOrderStatusBarData.queryWasExecuted ? (
          <TopOrderStatusBar data={topOrderStatusBarData} />
        ) : (
          !noStoreSelected && !hideTsm && <TopServiceMode />
        )}
      </CenteredRowContainer>
    </MobileHeaderContainer>
  );
});
